import { ActionTree } from 'vuex';
import { RootState, SingleFilter } from '@/store/types';
import api from '@/api';
import * as vacationRequests from '@/api/requests/vacation/vacations';
import * as vacationTypeRequests from '@/api/requests/vacation/vacation-types';
import * as userVacationHourRequests from '@/api/requests/vacation/user-vacation-hours';
import { VacationResource, UserVacationHourResource, VacationExcelQueryParams, VacationQueryParams, UserVacationHourRemainingsQueryParams, VacationTypeQueryParams} from '@/api/requests/vacation/types';
import { VacationState } from './types';
import Vacation from '@/api/models/vacation';
import UserVacationHour from '@/api/models/vacation';

export const actions: ActionTree<VacationState, RootState> = {

    // Vacations
    
    /**
     * Fetch all vacations.
     * @param commit
     */
    async fetchVacations({ commit, getters }) {
        const response = await api.call(vacationRequests.ALL(getters.httpFilters));
        commit('setVacations', response.data.data);
    },

    /**
     * Create a new vacation.
     *
     * @param commit
     * @param vacation
     */
    async createVacation({ commit }, vacation: Vacation) {
        const response = await api.call(vacationRequests.STORE(vacation.toRequest()));
        const resource: VacationResource = response.data.data;

        commit('addVacation', resource);
    },

    /**
     * Delete vacation by id.
     * @param commit
     */
    async deleteVacation({ commit }, vacationId: number) {
        await api.call(vacationRequests.DELETE(vacationId));
        commit('removeVacation', vacationId);
    },

    /**
     * Update an existing vacation.
     *
     * @param commit
     * @param vacation
     */
    async updateVacation({ commit }, vacation: Vacation) {
        if (!vacation.id) {
            throw new Error('No vacation id provided.');
        }
        const response = await api.call(vacationRequests.UPDATE(vacation.id, vacation));
        const resource: VacationResource = response.data.data;

        commit('updateVacation', resource);
    },

    /**
     * Update an existing vacation.
     *
     * @param commit
     * @param vacation
     */
    async updateVacationStatus({ commit }, vacation: Vacation) {
        if (!vacation.id) {
            throw new Error('No vacation id provided.');
        }
        const response = await api.call(vacationRequests.UPDATE_STATUS(vacation.id, vacation.status));
        const resource: VacationResource = response.data.data;

        commit('updateVacation', resource);
    },

    /**
     * Set active vacation.
     * @param commit
     * @param vacationId
     */
    async setActiveVacation({ commit, getters }, vacationId: number) {
        if (!getters.isVacationLoaded(vacationId)) {
            const response = await api.call(vacationRequests.FIND(vacationId));
            commit('upsertVacation', response.data.data);
        }
        commit('setActiveVacationId', vacationId);
    },

    /**
     * Get duration of a vacation.
     * @param commit
     */
    async getVacationDuration({ commit, getters }, params?: VacationQueryParams) {
        const response = await api.call(vacationRequests.DURATION(params));
        return response.data;
    },

    /**
     * Get excel of company vacations.
     * @param commit
     */
    async getVacationsExcel({ commit, getters }, params?: VacationExcelQueryParams) {
        const response = await api.call(vacationRequests.EXCEL(params));
        return response;
    },

    // User vacation hours

    /**
     * Fetch all user vacation hours.
     * @param commit
     */
    async fetchUserVacationHours({ commit, getters }) {
        const response = await api.call(userVacationHourRequests.ALL(getters.httpFilters));
        commit('setUserVacationHours', response.data.data);
    },

      /**
     * Get vacation hour remainings.
     * @param commit
     */
      async getRemainings({ commit, getters }, params?: UserVacationHourRemainingsQueryParams) {
        const response = await api.call(userVacationHourRequests.REMAININGS(params));
        return response.data;
    },

    /**
     * Create a new user vacation hour.
     *
     * @param commit
     * @param userVacationHour
     */
    async createUserVacationHour({ commit }, userVacationHour: UserVacationHour) {
        const response = await api.call(userVacationHourRequests.STORE(userVacationHour.toRequest()));
        const resource: UserVacationHourResource = response.data.data;

        commit('addUserVacationHour', resource);
    },

    /**
     * Delete user vacation hour by id.
     *
     * @param commit
     * @param userVacationHourIds
     */
    async deleteUserVacationHour({ commit }, ...userVacationHourIds: number[]) {
        await Promise.all(userVacationHourIds.map(async (userVacationHourId) => {
            await api.call(userVacationHourRequests.DELETE(userVacationHourId));
            commit('removeUserVacationHour', userVacationHourId);
        }));
    },

    /**
     * Update an existing user vacation hour.
     *
     * @param commit
     * @param userVacationHour
     */
    async updateUserVacationHour({ commit }, userVacationHour: UserVacationHourResource) {
        if (!userVacationHour.id) {
            throw new Error('No user vacation hour id provided.');
        }
        const response = await api.call(userVacationHourRequests.UPDATE(userVacationHour.id, userVacationHour));
        const resource: UserVacationHourResource = response.data.data;

        commit('updateUserVacationHour', resource);
    },

    /**
     * Set active user vacation hour.
     * @param commit
     * @param userVacationHourId
     */
    async setActiveUserVacationHour({ commit, getters }, userVacationHourId: number) {
        const response = await api.call(userVacationHourRequests.FIND(userVacationHourId));
        commit('upsertUserVacationHour', response.data.data);
        commit('setActiveUserVacationHourId', userVacationHourId);
    },

    // Vacation Types

     /**
     * 
     * Fetch vacation types.
     * @param commit
     */
     async fetchVacationTypes({ commit, getters }, params?: VacationTypeQueryParams) {
        const response = await api.call(vacationTypeRequests.ALL(params));
        return response.data.data;
        // commit('setVacationTypes', response.data.data);
    },

    // Filters

    /**
      * Reset a single filter.
      * @param commit
      * @param key
      */
    resetFilter({ commit, dispatch }, key: string) {
        commit('resetFilter', key);
        dispatch('fetchVacations');
    },

    /**
     * Reset all filters.
     * @param commit
     */
    resetFilters({ commit, dispatch }) {
        commit('resetFilters');
        dispatch('fetchVacations');
    },

     /**
     * Set filters.
     * @param commit
     * @param filters
     */
     setFilters({ commit, dispatch }, filters: SingleFilter[]) {
        filters.map((filter) => commit('setFilter', filter));
        dispatch('fetchVacations');
    },

};
